<script setup lang="ts">
/**
 * Maps the API's run/gate vocabulary onto the BG §18.4 status colours and hands off to ZhStatusPill,
 * which owns the colour, the glyph and the pulse. The mapping itself lives in `@/lib/statusTone`,
 * because the run table, the execution graph and the sidebar all have to agree with it.
 *
 * The `execution-status-*` classes are kept on the root. They no longer carry any styling — the
 * pill does that — but they are a stable hook the sidebar's tests and anything else reading the
 * DOM already depend on, and there is nothing to gain by renaming them. They always name the tone
 * the pill actually renders.
 *
 * `tone` is for a caller that paints the state in a colour other than its own: the sidebar shows run
 * states neutral outside a page that keeps its colours (N5, #1745). Left out, as everywhere else in the portal, the status decides.
 */
import { ZhStatusPill } from '@zerohuman/ui'
import { computed } from 'vue'
import { type RunStatusTone, statusLabel, statusTone } from '@/lib/statusTone'

const props = defineProps<{ status: string; count?: number; tone?: RunStatusTone }>()

const pillTone = computed(() => props.tone ?? statusTone(props.status))
</script>

<template>
    <ZhStatusPill
        :tone="pillTone"
        :count="count"
        class="execution-status"
        :class="[`execution-status-${pillTone}`, { 'execution-status-count': count !== undefined }]"
        :data-status="status"
    >
        {{ count ?? statusLabel(status) }}
    </ZhStatusPill>
</template>
